/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './CarouselCard.scss';

import cx from 'classnames';

import {
    Icon,
    FauxImage,
    FauxUnavailableImage,
    Text,
    StarRating,
    toTitleCase,
} from 'sarsaparilla';
import * as MapHelper from 'shared-ui/maps/helpers/mapHelper';

import {
    AvailabilityBadge,
    getAvailabilityDatesToDisplay,
    addAvailabilityDatesToLocalStorage,
} from './CarouselCard.utils';
import { type CarouselCardItem, validateAvailability } from './CarouselCard.types';

export interface CarouselCardProps extends Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'onClick'
> {
    item: CarouselCardItem;
    gaTrackingId?: string;
    gaTrackingValue?: string;
    showAvailDates?: boolean;
    onClick?: () => void;
    x?: number;
    showRating?: boolean;
}

export function CarouselCard({
    item,
    gaTrackingId,
    gaTrackingValue,
    className,
    onClick,
    showAvailDates,
    x = 1.5,
    showRating = true,
    ...rest
}: CarouselCardProps) {
    const cardTitle = toTitleCase(item.name ?? 'Unnamed Site');
    const url = MapHelper.getTargetUri(item);

    // location
    const hasCity = item.city === undefined ? '' : `${item.city}`;
    const hasStateCode = item.state_code === undefined ? '' : item.state_code;
    const hasComma = hasCity && hasStateCode ? ',' : '';
    const location = `${hasCity}${hasComma} ${hasStateCode}`;

    const datesString = showAvailDates ? getAvailabilityDatesToDisplay(item) : '';

    return (
        <div className={cx('nav--carousel-card', className)}>
            <div>
                <div className="image-group">
                    {item.preview_image_url ? (
                        <FauxImage
                            src={item.preview_image_url}
                            aspectRatio={`${x}:1`}
                            className="image"
                        />
                    ) : (
                        <FauxUnavailableImage aspectRatio={`${x}:1`} className="image" />
                    )}

                    {showAvailDates && datesString ? (
                        <div className="dates">
                            <Icon iconName="calendar" size="sm" className="dates-icon" />{' '}
                            {datesString}
                        </div>
                    ) : (
                        <AvailabilityBadge
                            availability={validateAvailability(item.availability)}
                        />
                    )}
                </div>

                <div className="content-group">
                    <a
                        href={url}
                        data-ga-tracking-id={gaTrackingId}
                        data-ga-tracking-value={gaTrackingValue}
                        onClick={(event) => {
                            event.preventDefault();

                            addAvailabilityDatesToLocalStorage(item);

                            if (onClick) {
                                try {
                                    onClick();
                                } catch (error) {
                                    // Eat error and just navigate
                                    // eslint-disable-next-line no-console
                                    console.warn(error);
                                }

                                window.location.href = url;
                            }
                        }}
                        {...rest}
                    >
                        {cardTitle}
                    </a>

                    {location ? (
                        <Text tag="div" size="sm" color="gray" className="parent mt-half">
                            {location}
                        </Text>
                    ) : null}

                    {showRating && item.average_rating ? (
                        <div className="star-rating mt-half">
                            <StarRating
                                rating={item.average_rating ?? 0}
                                reviewCount={item.number_of_ratings ?? 0}
                                size="sm"
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
